<template>
	<div class="wrapper review-purchase">
		<div class="murk">
			<balanceInfo
				:title="$t('wallet.reviewPurchase')"
				show-switch-account
				:switch-account-tracking="switchAccountTracking"
				:is-in-game-currency-tab="isInGameCurrencyReviewType && { gamePoints, gameCurrency }"
			/>

			<div v-if="region === 'global'" class="history-nav-tabs">
				<div
					v-for="type in reviewTypes"
					:key="type"
					class="history-nav-tabs__option"
					:class="{ active: type === reviewType }"
					@click="handleReviewTypeClick(type)"
				>
					<span>{{ $t(`wallet.${type}`) }}</span>
				</div>
			</div>

			<div v-if="!isInGameCurrencyReviewType" class="purchases-header">
				<div v-if="region === 'global' || region === 'tw'" class="purchase-tabs-list activity-label" :class="purchaseType">
					<div
						v-for="(type, $index) in purchaseTypes"
						:key="$index"
						:class="{ selected: type === purchaseType }"
						@click="handlePurchaseTab(type)"
					>
						<span>{{ $t(`history.purchase-type.${type}`) }}</span>
					</div>
				</div>

				<div v-if="purchaseType === 'nx'" class="sort-by-container" :class="region === 'global' ? 'west' : 'non-west'">
					<div v-if="region !== 'global'" class="sort-by-game">
						<div class="activity-label heading-label">
							{{ $t('wallet.sort-by-game') }}
						</div>

						<formSelect
							v-model="filterGame"
							class="form-select-wrapper"
							:input-small="true"
							:options="filterGameOptions"
							name="selectGameFilter"
							options-value="serviceCode"
							options-label="gameName"
							close-on-select
							dont-localize
						>
						</formSelect>
					</div>

					<div class="sort-by-type">
						<div v-if="purchaseType === 'nx' && region === 'global'" class="activity-label heading-label">
							{{ $t('wallet.purchaseActivities') }}
						</div>

						<div v-if="region !== 'global'" class="activity-label heading-label">
							{{ $t('wallet.sort-by-purchase-type') }}
						</div>
						<formSelect
							v-model="filterType"
							class="form-select-wrapper"
							:input-small="true"
							:options="filterTypesOptions"
							name="selectFilter"
							options-value="val"
							options-label="label"
							string-selector="wallet"
							close-on-select
						>
						</formSelect>
					</div>
				</div>
			</div>

			<div v-else class="in-game-currency-header">
				<div class="in-game-currency-header__game">
					<div class="in-game-currency-header__game__title">MAPLESTORY</div>

					<div class="in-game-currency-header__game__regions">
						<div
							v-for="item in maplestoryRegionOptions"
							:key="item"
							:class="[`region-${item.toLowerCase()}`, { active: item === maplestoryRegion }]"
							@click="maplestoryRegion = item"
						/>
					</div>
				</div>
				<div class="sort-by-date">
					<div class="activity-label heading-label">{{ $t('wallet.sort-by-date') }}:</div>
					<formSelect
						v-model="fromDate"
						class="form-select-wrapper"
						:input-small="true"
						:options="fromDateOptions"
						name="selectFilter"
						options-value="val"
						options-label="label"
						string-selector="wallet"
						close-on-select
						dont-localize
					>
					</formSelect>
				</div>
			</div>

			<div class="table-wrapper" :class="purchaseType">
				<v-client-table :data="rows" :columns="columns" :options="tableOptions" :class="{ error }">
					<span
						v-if="props.row.clickable"
						slot="orderNumber"
						slot-scope="props"
						class="clickable"
						@click="openSummaryModal(props.row.orderNumber)"
						>{{ props.row.orderNumber }}</span
					>
					<span v-else slot="orderNumber" slot-scope="props">{{ props.row.orderNumber }}</span>
					<span v-if="props.row.status === 'pending'" slot="amount" slot-scope="props">
						<div class="pending-amount">
							<img src="@/images/svg/ico_warning.svg" alt="pending" />
							{{ props.row.amount }}
						</div>
					</span>
					<span v-else slot="amount" slot-scope="props">{{ props.row.amount }}</span>
				</v-client-table>

				<div v-if="error" class="error-message">
					<div>{{ $t('wallet.error-loading-data') }}</div>
					<i18n path="wallet.if-problem-persist" tag="div">
						<template v-slot:support>
							<a :href="supportLink">{{ $t('wallet.support') }}</a>
						</template>
					</i18n>
				</div>
			</div>

			<div id="history-paginate">
				<paginate
					v-model="page"
					:page-count="totalPageCount"
					:page-range="5"
					:page-class="'page-number'"
					:prev-class="'pagination-prev-page'"
					:next-class="'pagination-next-page'"
					:container-class="'pagination'"
					:click-handler="fetchPage"
				/>
			</div>
		</div>

		<nonNxOrderSummary
			v-if="orderSummaryModal.transactionId && purchaseType === 'non-nx'"
			:show-modal="orderSummaryModal.show"
			:close-action="closeOrderSummaryModal"
			:transaction-id="orderSummaryModal.transactionId"
		/>

		<div class="modal" :class="{ hide: !historyLoading }">
			<div id="main-spinner" class="page-spinner-wrapper main-spinner">
				<span class="spinner-circle"></span>
			</div>
		</div>
	</div>
</template>

<script>
import formSelect from '@arena/toolkit/src/components/common/form-select';
import balanceInfo from '@/components/common/balance-info';
import { history } from '../plugins/WalletAPI';
import nonNxOrderSummary from './non-nx-order-summary';
import store from '../store';
import supportLinks from '@/config/support-links';
import moment from 'moment';
import axios from 'axios';

export default {
	name: 'History',
	components: {
		balanceInfo,
		formSelect,
		nonNxOrderSummary,
	},
	metaInfo() {
		return {
			titleTemplate: `%s | ${this.$t('wallet.reviewPurchase')}`,
		};
	},
	data() {
		return {
			loadingLabel: this.$t('wallet.loading-history', this.$route.params.locale),
			historyEntries: [],
			tableOptions: {
				skin: 'table',
				columnsClasses: {
					date: 'date',
					orderNumber: 'order-number',
					description: 'description',
					amount: 'amount',
				},
				perPage: 10,
				sortable: [],
				texts: {
					filter: 'Filter:',
					filterPlaceholder: '',
					count: '',
					noResults: this.$t('wallet.no-records', this.$route.params.locale),
				},
				filterable: [],
				headings: {
					date: this.$t('wallet.date', this.$route.params.locale),
					orderNumber: this.$t('wallet.order-number', this.$route.params.locale),
					description: this.$t('wallet.description', this.$route.params.locale),
					amount: this.$t('wallet.amount', this.$route.params.locale),
				},
			},
			totalRecords: 10,
			filterType: 'all',
			page: 1,
			historyLoading: false,
			purchaseTypes: ['nx', 'non-nx'],
			purchaseType: 'nx',
			orderSummaryModal: {
				show: false,
				transactionId: null,
			},
			filterGame: 'all',
			filterGameOptions: [],
			reviewTypes: ['purchases', 'in-game-currency'],
			reviewType: 'purchases',
			fromDate: 3, // last n months
			maplestoryRegion: 'NA',
			maplestoryRegionOptions: ['NA', 'EU'],
			gamePoints: 0,
			gameCurrency: '',
			error: false,
			transactionId: '',
		};
	},
	computed: {
		isInGameCurrencyReviewType() {
			return this.reviewType === 'in-game-currency';
		},
		region() {
			return environment.region;
		},
		totalPageCount() {
			return Math.ceil(this.totalRecords / this.tableOptions.perPage) || 1;
		},
		columns() {
			return ['date', ...(!this.isInGameCurrencyReviewType ? ['orderNumber'] : []), 'description', 'amount'];
		},
		rows() {
			return this.historyEntries.map((entry) => {
				if (this.isInGameCurrencyReviewType) {
					return {
						date: new Date(entry.chargeDate).toLocaleDateString(`${this.$i18n.locale}-u-ca-gregory`),
						description: entry.maplepointDescription,
						amount: `${entry.maplepointAmount > 0 ? '+' : ''}${this.$n(entry.maplepointAmount)} ${this.gameCurrency}`,
					};
				}

				let sign;
				if (this.purchaseType === 'non-nx') {
					sign = '';
				} else if (entry.type === 'in') {
					sign = '+';
				} else {
					sign = '-';
				}
				return entry.currency === 'NX'
					? {
							// eslint-disable-line no-nested-ternary
							date: new Date(entry.date).toLocaleDateString(`${this.$i18n.locale}-u-ca-gregory`),
							orderNumber: entry.orderNumber,
							description: entry.description,
							amount: `${sign}${this.$n(entry.amount)} ${entry.currency}`,
							status: entry.status,
					  }
					: entry.currency === 'TWD'
					? {
							date: new Date(entry.date).toLocaleDateString(`${this.$i18n.locale}-u-ca-gregory`),
							orderNumber: entry.orderNumber,
							description: entry.description,
							amount: `${sign}${this.$n(entry.amount)} ${
								this.purchaseType === 'non-nx' ? entry.currency : this.$t('wallet.points')
							}`,
							status: entry.status,
					  }
					: {
							date: new Date(entry.date).toLocaleDateString(`${this.$i18n.locale}-u-ca-gregory`),
							orderNumber: entry.orderNumber,
							description: entry.description,
							amount: `${sign}${this.$n(entry.amount, 'currency', entry.currency)}`,
							clickable: this.purchaseType === 'non-nx',
							status: entry.status,
					  };
			});
		},
		filterTypes() {
			return [
				{ label: 'all', val: 'all' },
				{ label: this.region === 'global' ? 'buy-nx' : 'add-balance', val: 'in' },
				{ label: 'in-game-item-purchase', val: 'out' },
			];
		},
		filterTypesOptions() {
			if (this.region !== 'global') {
				// if in sort by game is selected a game then disabled the 'Add Balance' purchase type option
				const filterTypes = this.filterTypes.map((filter) => {
					return {
						label: filter.label,
						val: filter.val,
						disabled: this.filterGame !== 'all' && filter.val === 'in' ? true : false,
					};
				});
				return filterTypes;
			}
			return this.filterTypes;
		},
		fromDateOptions() {
			return [
				{
					label: this.$t('wallet.last-3-months'),
					val: 3, //months
				},
				{
					label: this.$t('wallet.last-6-months'),
					val: 6, //months
				},
				{
					label: this.$t('wallet.last-year'),
					val: 12, //months
				},
				{
					label: this.$t('wallet.last-2-years'),
					val: 24, //months
				},
			];
		},
		supportLink() {
			return supportLinks[this.region];
		},
	},
	watch: {
		filterType(vals) {
			const findItem = this.filterTypes.find((item) => item.val === vals);
			const label = findItem.label;
			switch (this.region) {
				case 'th':
					this.$a2s.click('review_npoint_filter_type', {
						name: 'filter_type',
						value: this.$t(`wallet.${label}`),
					});
					break;
				default:
					this.$a2s.click('review_purchase_filter', {
						name: 'purchase_filter',
						value: this.$t(`wallet.${label}`),
					});
					break;
			}
			if (this.filterType === this.$route.query.filterType) return;
			this.$router.push({ query: { ...this.$route.query, filterType: this.filterType, page: 1 } });
		},
		purchaseType() {
			if (this.purchaseType === this.$route.query.purchaseType) return;
			this.$router.push({ query: { ...this.$route.query, purchaseType: this.purchaseType, page: 1 } });
		},
		filterGame(val) {
			this.$a2s.click('review_npoint_filter_game', {
				name: 'filter_game',
				value: val,
			});
			if (this.filterGame === this.$route.query.filterGame) return;
			this.$router.push({ query: { ...this.$route.query, filterGame: this.filterGame, page: 1 } });
		},
		reviewType(value) {
			if (this.reviewType === this.$route.query.reviewType) return;
			this.historyEntries = [];
			this.totalRecords = 0;
			this.$router.push({ query: { ...this.$route.query, reviewType: value, page: 1 } });
		},
		fromDate(vals) {
			const findItem = this.fromDateOptions.find((item) => item.val === vals);
			const label = findItem.label;
			this.$a2s.click('review_ingame_time_filter', {
				name: 'time_filter',
				value: label,
			});
			if (this.fromDate === parseInt(this.$route.query.from, 10)) return;
			this.$router.push({ query: { ...this.$route.query, page: 1, from: this.fromDate } });
		},
		maplestoryRegion(vals) {
			this.$a2s.click('review_ingame_country_filter', {
				name: 'country_filter',
				value: vals === 'NA' ? 'US' : 'EU',
			});
			if (this.maplestoryRegion === this.$route.query.msr) return;
			this.$router.push({ query: { ...this.$route.query, page: 1, msr: this.maplestoryRegion } });
		},
	},
	created() {
		let query = this.$route.query;
		if (query.purchaseType && !this.purchaseTypes.includes(query.purchaseType)) {
			delete query.purchaseType;
		}
		if (query.reviewType && !this.reviewTypes.includes(query.reviewType)) {
			delete query.reviewType;
		}
		if (
			query.reviewType === 'in-game-currency' &&
			query.from &&
			!this.fromDateOptions.find((item) => item.val === parseInt(query.from, 10))
		) {
			delete query.from;
		}
		if (query.transactionId) {
			this.transactionId = query.transactionId;
		}
		query = Object.assign(
			{
				// default
				page: this.page,
				filterType: this.filterType,
				transactionId: this.transactionId,
				...(this.region === 'global' && { purchaseType: this.purchaseType, reviewType: this.reviewType, from: this.fromDate }),
				...(this.region !== 'global' && { filterGame: this.filterGame }),
			},
			query,
			{ _t: Date.now() }
		);
		this.$router.replace({
			name: this.$route.name,
			query,
		});
	},
	mounted() {
		this.$a2s.view('review_imp');
	},
	methods: {
		fetchPage(page) {
			if (page > 1) {
				const len = this.historyEntries.length;
				if (len > 0) {
					this.transactionId = this.historyEntries[len - 1].orderNumber;
				}
			} else if (page) {
				this.transactionId = '';
			}

			this.$router.push({ query: { ...this.$route.query, page, transactionId: this.transactionId } });
		},
		changeQuerystrings(query) {
			location.href = `${location.pathname}?${Object.keys(query)
				.map((key) => `${key}=${query[key]}`)
				.join('&')}`;
		},
		openSummaryModal(orderNumber) {
			this.orderSummaryModal.transactionId = orderNumber;
			this.orderSummaryModal.show = true;
		},
		closeOrderSummaryModal() {
			this.orderSummaryModal.show = false;
			this.orderSummaryModal.transactionId = null;
		},
		switchAccountTracking() {
			dataLayer.push({
				event: 'Review Purchase Switch Account Click',
			});
		},
		handlePurchaseTab(type) {
			this.purchaseType = type;
			this.$a2s.click('review_method_tab');
		},
		handleReviewTypeClick(type) {
			const format = type.toUpperCase().replace('-CURRENCY', ' CURRENCY');
			this.$a2s.click('review_purchase_tab', {
				name: 'purchase_tab',
				value: format,
			});
			this.reviewType = type;
		},
	},
	async beforeRouteUpdate(to, from, next) {
		this.error = false;
		if (to.query.page) this.page = parseInt(to.query.page, 10);
		if (to.query.filterType) this.filterType = to.query.filterType;
		if (to.query.purchaseType) this.purchaseType = to.query.purchaseType;
		if (to.query.reviewType && this.region === 'global') this.reviewType = to.query.reviewType;
		if (to.query.from) this.fromDate = parseInt(to.query.from, 10);
		if (to.query.filterGame) this.filterGame = to.query.filterGame;
		if (to.query.msr) this.maplestoryRegion = to.query.msr;

		this.historyLoading = true;
		this.$store.commit('startLoadingOverlay', this.loadingLabel);
		this.$eventBus.$emit('show-loading-overlay');
		this.filterGameOptions = [{ gameName: this.$t('wallet.all'), serviceCode: 'all' }]; // hardcoded 'All' option
		try {
			if (!this.isInGameCurrencyReviewType) {
				const response = await history(
					this.purchaseType,
					this.page,
					this.filterType,
					this.filterGame,
					this.tableOptions.perPage,
					this.transactionId
				);

				if (response.data.account) store.commit('setAccountData', response.data.account);
				this.historyEntries = response.data.transactions;
				this.totalRecords = response.data.total;
				const gameList = response.data.services;
				if (gameList) {
					gameList.forEach((item) => {
						this.filterGameOptions.push({
							gameName: item.gameName,
							serviceCode: item.serviceCode,
							disabled: this.filterType === 'in' ? true : false, // if the sort by purchase type is 'Add Balance' then all game will be disabled
						});
					});
				}
			} else {
				const fromDate = moment()
					.subtract(this.fromDate, 'months')
					.format('YYYY-MM-DD');
				const toDate = moment().format('YYYY-MM-DD');

				const response = await axios.get('/api/web-game/v1/games/maplestory/maplepoint-history', {
					params: { pageIndex: this.page, fromDate, toDate, region: this.maplestoryRegion },
					withCredentials: true,
				});

				this.gamePoints = response.data.maplepoint;
				this.gameCurrency = response.data.currencyType;
				this.historyEntries = response.data.maplepointChargeLog ? response.data.maplepointChargeLog : [];
				this.totalRecords = response.data.totalRowCount;
				if (!this.$store.getters.email) this.$store.commit('setAccountData', { email: response.data.email });
			}

			this.$eventBus.$emit('hide-loading-overlay');
			this.historyLoading = false;
			next();
		} catch (error) {
			this.error = true;
			this.historyEntries = [];
			this.totalRecords = 0;
			this.$eventBus.$emit('hide-loading-overlay');
			this.historyLoading = false;
			next();
		}
	},
};
</script>

<style lang="less">
@blue: #0077ff;

.review-purchase {
	.form-select-wrapper {
		width: 160px;
		.form-group {
			margin-bottom: 0px;

			label {
				display: none;
			}
			.form-control {
				height: 42px;
				padding-bottom: 0px;
				position: relative;
				.caret {
					top: 50%;
					transform: translateY(-50%);
				}
			}
			.dropdown-menu {
				height: auto;
			}
			&.mobile {
				height: 42px;
				margin-top: 0px;
				.placeholder {
					display: none;
				}
				.form-control {
					padding-top: 2px;
				}
				.caret {
					top: 50%;
					transform: translateY(-50%);
				}
			}
			@media (min-width: 601px) {
				.form-control {
					font-size: 12px;
					padding-top: 5px;
				}

				&.mobile,
				.form-control {
					height: 25px;
				}

				.dropdown-menu {
					a {
						font-size: 12px;
					}
				}
			}
		}
	}

	.history-nav-tabs {
		display: flex;
		flex-wrap: wrap;
		margin-top: 20px;
		text-transform: uppercase;
		font-size: 16px;

		&__option {
			display: flex;
			justify-content: center;
			align-items: center;
			text-align: center;
			flex: 0 0 50%;
			max-width: 50%;
			position: relative;
			padding: 15px 10px 13px 10px;
			font-weight: bold;
			background-color: #4f4f4f;
			color: rgba(#fff, 0.25);
			transition: all 0.2s;

			> span {
				margin: auto 10px;
			}
			&:first-child {
				border-top-left-radius: 6px;
				border-bottom-left-radius: 6px;
			}
			&:last-child {
				border-top-right-radius: 6px;
				border-bottom-right-radius: 6px;
			}

			&:hover {
				background-color: #7b7b7b;
				color: rgba(#fff, 0.5);
				cursor: pointer;
				transition: all 0.2s;
			}
			&.active {
				background-color: #fff;
				color: @blue;
				&:hover {
					cursor: default;
				}
			}
		}
		@media (max-width: 767px) {
			font-size: 14px;
			&__option {
				padding-top: 8px;
				padding-bottom: 8px;
			}
		}
	}

	.purchase-tabs-list {
		display: flex;
		flex-wrap: wrap;
		padding-top: 16px;
		padding-bottom: 15px;

		div {
			text-transform: uppercase;
			font-weight: bold;
			&.selected {
				color: @blue;
				pointer-events: none;
			}
			&:not(.selected) {
				cursor: pointer;
				span {
					color: white;
					opacity: 0.5;
					&:hover {
						opacity: 0.8;
					}
				}
			}
		}
		div + div {
			border-left: 2px solid gray;
			padding-left: 20px;
			margin-left: 20px;
		}

		@media (max-width: 600px) {
			margin-right: 0px;
			line-height: 1.2;
			&.nx {
				padding-bottom: 12px;
			}
		}

		@media (max-width: 350px) {
			flex-direction: column;
			div + div {
				border-left: none;
				padding-top: 5px;
				padding-left: 0px;
				margin-left: 0px;
			}
		}
	}

	.purchases-header,
	.in-game-currency-header {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		align-items: center;
		padding-top: 20px;
		padding-bottom: 20px;

		.sort-by-container {
			display: flex;
			flex-wrap: wrap;
			justify-content: flex-end;
			margin-left: auto;
		}

		.sort-by-game,
		.sort-by-type,
		.sort-by-date {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			padding-top: 10px;
			padding-bottom: 10px;
		}

		.sort-by-date {
			text-transform: uppercase;
			margin-left: auto;
			.form-control {
				text-transform: uppercase;
			}
		}
		.activity-label {
			color: #d9d9d9;
			margin-right: 20px;
		}
		.sort-by-type {
			.activity-label {
				margin-left: 25px;
			}
		}

		@media (max-width: 767px) {
			padding-top: 10px;
			padding-bottom: 10px;
		}
	}

	.in-game-currency-header {
		&__game {
			display: flex;
			align-items: center;
			color: #fff;
			&__title {
				font-size: 18px;
				margin-right: 15px;
			}
			&__regions {
				display: flex;

				.region {
					&-na,
					&-eu {
						width: 30px;
						height: 22px;
						background-size: contain;
						background-repeat: no-repeat;
						background-position: center;
						box-shadow: 0px 0px 5px 0px #646464;
						opacity: 0.37;
						transition: opacity 0.2s;

						@media (max-width: 600px) {
							width: 42px;
							height: 32px;
						}

						&:hover {
							cursor: pointer;
							opacity: 1;
							transition: opacity 0.2s;
						}

						&.active {
							opacity: 1;
						}
					}

					&-eu {
						background-image: url('~@/images/svg/eu.svg');
						background-size: cover;
					}
					&-na {
						background-image: url('~@/images/svg/us.svg');
					}
				}
			}
		}
		@media (max-width: 600px) {
			&__game {
				padding-top: 5px;
			}
		}
		@media (max-width: 600px) {
			&__game {
				flex: 0 0 100%;
				max-width: 100%;
				justify-content: space-between;
			}
			.sort-by-date {
				width: 100%;
				margin-top: 7px;
			}
		}
	}

	.table-wrapper {
		margin: 0px auto 30px;
		&.coupon {
			.amount {
				display: none;
			}
		}
		.VueTables {
			.row {
				margin: 0;
				.col-md-12:first-child {
					width: 100%;
					div {
						float: right !important;
					}
				}
				.VueTables__limit {
					display: none;
				}
			}
			.VueTables__no-results {
				td {
					text-align: center !important;
				}
			}
			.table {
				thead {
					th {
						padding-top: 18px;
						padding-bottom: 20px;
						border-bottom: 2px solid #999;
					}
				}
				.date {
					width: 120px;
					@media (min-width: 768px) {
						padding-left: 20px;
					}
				}
				.order-number {
					width: 37%;
					.clickable {
						cursor: pointer;
						text-decoration: underline;
					}
				}
				.description {
					@media screen and (max-width: 767px) {
						white-space: normal;
					}
				}
				.amount {
					white-space: nowrap;
					@media (min-width: 768px) {
						padding-right: 20px;
					}

					.pending-amount {
						display: flex;
						align-items: center;
						justify-content: flex-end;
						gap: 2px;

						@media (max-width: 768px) {
							justify-content: flex-start;
						}
					}
				}
			}

			&.error {
				.VueTables__no-results {
					display: none;
				}
			}
		}
		.VueTables__highlight {
			b {
				color: #4ca1ff;
			}
		}
	}

	.error-message {
		color: #ff1744;
		text-align: center;
		padding-top: 13px;
		padding-bottom: 27px;
		border-bottom: 1px solid #383838;
		line-height: 1.2;
		a {
			border-bottom: 1px solid @blue;
			transition: all 0.3s;
			color: @blue;
			&:hover {
				color: #4ca1ff;
				border-bottom: 1px solid #4ca1ff;
				transition: all 0.3s;
			}
		}

		@media (max-width: 767px) {
			border-top: 2px solid #999;
			padding-top: 27px;

			> div {
				display: inline;
				padding-left: 2px;
				padding-right: 2px;
			}
		}
	}

	#history-paginate {
		text-align: center;
		.pagination {
			.pagination-prev-page,
			.pagination-next-page {
				&.disabled {
					opacity: 0.5;
				}
				a {
					background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20.25 36'><path fill='%23fff' d='M19.29,24l14.2-14.2a2.23,2.23,0,1,0-3.16-3.15L14.55,22.45a2.23,2.23,0,0,0,0,3.15L30.33,41.37a2.23,2.23,0,1,0,3.16-3.15Z' transform='translate(-13.9 -6.03)'/></svg>")
						no-repeat top left;
					height: 14px;
					width: 14px;
					color: transparent;
					font-size: 0;
					margin-top: 9px;
					opacity: 0.5;
					transition: 0.3s ease;
					&:hover,
					&:focus {
						opacity: 1;
						color: transparent;
						background-color: transparent;
					}
				}
			}
			.pagination-prev-page {
				a {
					background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20.25 36'><path fill='%23fff' d='M19.29,24l14.2-14.2a2.23,2.23,0,1,0-3.16-3.15L14.55,22.45a2.23,2.23,0,0,0,0,3.15L30.33,41.37a2.23,2.23,0,1,0,3.16-3.15Z' transform='translate(-13.9 -6.03)'/></svg>")
						no-repeat top left;
				}
			}
			.pagination-next-page {
				a {
					background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20.25 36'><path fill='%23fff' d='M33.49,22.45,17.7,6.68a2.23,2.23,0,0,0-3.15,3.16L28.75,24,14.55,38.22a2.23,2.23,0,1,0,3.15,3.15L33.49,25.6A2.23,2.23,0,0,0,33.49,22.45Z' transform='translate(-13.89 -6.03)'/></svg>")
						no-repeat top right;
				}
			}
			.page-number {
				> a {
					color: #d9d9d9;
					background-color: transparent;
					border-color: transparent;
					&:hover {
						background-color: #d9d9d9;
						border-color: #d9d9d9;
						color: #000;
					}
				}
				&.active {
					> a {
						background-color: #d9d9d9;
						border-color: #d9d9d9;
						color: #000;
					}
				}
			}
		}
	}
}

@media (max-width: 767px) {
	.review-purchase {
		.table-wrapper {
			margin: 0px auto 10px;

			.VueTables {
				th {
					color: #d9d9d9;
				}
				.table-responsive {
					border: none;
				}
				.form-control {
					display: inline-block;
					width: auto;
				}
				thead tr {
					display: none;
				}
				tbody {
					border-top: 2px solid #999;
					tr {
						border-bottom: 1px solid #383838;
						padding: 5px 0;
						td {
							&:first-child {
								padding-top: 20px;
							}
							&:last-child {
								padding-bottom: 20px;
								font-weight: bold;
							}
							display: block;
							padding: 7px 0;
							border-bottom: transparent !important;
							&.amount {
								color: #d9d9d9;
								text-align: left;
							}
							&.date,
							&.order-number,
							&.description,
							&.amount {
								width: 100% !important;
							}
						}
					}
				}
			}
		}

		.sort-by-container {
			&.non-west {
				.sort-by-game,
				.sort-by-type {
					flex-direction: column;
					align-items: flex-start;
					.activity-label {
						&.heading-label {
							margin-left: 0;
							margin-right: 0;
							margin-bottom: 10px;
						}
					}
				}
				.sort-by-game {
					margin-right: 25px;
				}
			}
		}
	}
}
@media (max-width: 600px) {
	.review-purchase {
		.form-select-wrapper {
			width: 100%;
		}
		.activity-label {
			&.heading-label {
				display: none;
			}
		}
		.sort-by-container {
			width: 100%;
			.sort-by-game,
			.sort-by-type {
				flex: 0 0 100%;
				max-width: 100%;
			}
			&.non-west {
				.sort-by-game,
				.sort-by-type {
					margin: 0;
					.activity-label {
						&.heading-label {
							display: block;
							margin-left: 0;
						}
					}
				}
				.sort-by-game {
					.form-group {
						margin-bottom: 0;
					}
				}
			}
		}
	}
	.VueTables {
		.VueTables__search {
			width: 100%;
		}
		.form-control {
			display: block;
			width: 100%;
		}
	}
}
</style>
